/**
 * Composition methods for the templates
 * 
 * Author: Andrea López <andrea.lopez@huumun.com>
 */
import { computed, onMounted, ref, watchEffect, inject, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { ILyraObject } from '@/core/modules/lyra/@types';
import { getLyraEndpoints, getLyraEndpoint } from '@/core/modules/lyra/services';

export function endpointIdComposition () {
    const route = useRoute();

    const endpointId = computed(() => {
        return route.params.endpointId;
    })
    
    return { endpointId }
}

export function templateLibraryComposition () {
	const store = useStore();

    /**
     * Endpoint
     */
    const route = useRoute();
    const $lyra = ref(<ILyraObject>{});

    const sectionId = computed((): any => {
        return route.params.sectionId;
    })
    const endpointId = computed((): any => {
        return route.params.endpointId;
    })
    const endpointObj = computed(() => {
        const sections = getLyraEndpoints('sections');
        const currentSection: any = sections.find((_: any) => _.endpointId === sectionId.value);
        if (!currentSection) return;
        return getLyraEndpoint(endpointId.value, currentSection.endpoints);
    })

    /**
     * Carousel
     */
    const slideActive = ref('');
    const slideIndexActive = ref(0);
    const isCarousel = computed((): boolean => {
        return endpointObj.value ? 'templates' in endpointObj.value && endpointObj.value.templates !== '' : false;
    })

    const carouselSlides = computed((): string[] => {
		if (!endpointObj.value) { return []; }
		
        const templatesList = endpointObj.value.templates ? endpointObj.value.templates : '';
        const templatesArray = templatesList.split(', ');

        return isCarousel.value ? templatesArray : [];
    })

    function setCarouselTemplateActive (index: number) {
        slideActive.value = carouselSlides.value[index];
        slideIndexActive.value = index;
    }

    function initCarousel () {
        setCarouselTemplateActive(0);
    }

	watch(() => route.path, () => {
		slideIndexActive.value = 0;
		initCarousel();
	});

    /**
     * Templates
     */
    const ready = ref(false);
    const template = ref('');
    const templateGrammar = ref({});
    
    const templateRows = computed((): any => {
        return  Object.keys(templateGrammar.value) || {};
    })
    
    watchEffect(async () => {
        ready.value = false;
        await queryData();
    })
    
    onMounted(async () => {
        ready.value = false;
        $lyra.value = await inject<ILyraObject>('$lyra') as ILyraObject;
        await queryData();

        if (isCarousel.value) {
            initCarousel();
        }
    })

    async function queryData () {
        if (!endpointObj.value) return;
        const endpoint = endpointObj.value;
        
        template.value = isCarousel.value ? slideActive.value : endpoint.template;
        const currentTemplate = template.value;

		if (!($lyra.value && 'endpoints' in $lyra.value)) {
			$lyra.value = store.getters.get_$lyra;
		}
        const response = await $lyra.value.endpoints({
            tierId: 'template',
            endpointId: currentTemplate,
        });

        if (!response.success || response.data.length < 1) { return; }
        
        const { data } = response;
        const [record] = data;
        const { datasets } = record;
        const [datum] = datasets;
        const { grammar } = datum;

        templateGrammar.value = (grammar && grammar.properties) ? grammar.properties : {};

        ready.value = true;
    }

    return { 
        ready, 
        templateRows, 
        endpointId, 
        templateGrammar, 
        carouselSlides, 
        isCarousel, 
        slideIndexActive,
        setCarouselTemplateActive
    }
}
